import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';

const navLinks = [
  { label: 'Features', href: '#features' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'FAQ', href: '#faq' },
];

export default function Navigation({ onScheduleCall }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav
      data-testid="navigation"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-black/60 backdrop-blur-xl border-b border-white/10'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <a href="#" data-testid="nav-logo" className="flex items-center gap-2">
            <img src="/logo.png" alt="webridgeAI" className="h-8 w-auto" />
          </a>

          {/* Desktop Links */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className="text-sm text-neutral-400 hover:text-white transition-colors duration-200 tracking-wide"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {link.label}
              </a>
            ))}
            <Button
              data-testid="nav-schedule-call-button"
              onClick={onScheduleCall}
              className="border border-white/20 bg-transparent text-white hover:bg-white/10 hover:border-white/40 text-sm px-5 py-2 rounded-full transition-all duration-200"
            >
              Book a Demo
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            data-testid="nav-mobile-toggle"
            className="md:hidden text-white"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-black/90 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-6 py-4 flex flex-col gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="text-sm text-neutral-400 hover:text-white transition-colors py-2"
                >
                  {link.label}
                </a>
              ))}
              <Button
                data-testid="nav-mobile-schedule-call"
                onClick={() => { onScheduleCall(); setMobileOpen(false); }}
                className="border border-white/20 bg-transparent text-white hover:bg-white/10 text-sm px-5 py-2 rounded-full w-fit"
              >
                Book a Demo
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
